import PARCEL_STAGE from '@pperform/pp-constants/parcelStage.constants';
import type { EddSource } from 'interfaces/edd.interface';
import type { ParcelEvent } from 'interfaces/ParcelEvent';

/**
 * Stage-level EDD visibility gate.
 *
 * Returns `false` when the parcel stage means EDD should never show
 * (delivered, or out_for_delivery when the flag is off), `true` otherwise.
 */
export const shouldShowEddByStage = (
  parcelStage: string,
  shouldShowForOutForDelivery: boolean,
): boolean => {
  if (parcelStage === PARCEL_STAGE.delivered) {
    return false;
  }
  if (
    parcelStage === PARCEL_STAGE.out_for_delivery &&
    !shouldShowForOutForDelivery
  ) {
    return false;
  }
  return true;
};

interface ShowEDDByEventsParams {
  events: ParcelEvent[];
  phaseKeys: string[];
  eventKeys: string[];
}

/**
 * Event-level EDD visibility gate.
 *
 * Checks whether the parcel's events contain any consumer-visible entry
 * that matches the provided phase or event key lists. Returns `false` when
 * a blocking event is found (EDD should hide), `true` otherwise.
 *
 * Contrast with `shouldShowEddByStage`:
 *   - `shouldShowEddByStage`  — stage-level gate (delivered / out_for_delivery)
 *   - `shouldShowEddByEvents` — event-level gate (specific phase/event keys)
 *
 * The caller (`useEddVisibility`) owns which key lists to pass per EDD source.
 */
export const shouldShowEddByEvents = ({
  events,
  phaseKeys,
  eventKeys,
}: ShowEDDByEventsParams): boolean => {
  if (!events?.length) return true;
  return !events.some(
    (event) =>
      event.eventTypeMasterData?.isConsumerVisible === true &&
      (phaseKeys.includes(event.eventTypeMasterData?.phaseKey) ||
        eventKeys.includes(event.eventTypeMasterData?.key)),
  );
};

/**
 * Checks if EDD has valid data (source and at least one date)
 *
 * @param eddValue - The EDD value object to validate
 * @returns true if EDD has valid data, false otherwise
 */
export function hasValidEdd(eddValue: EddSource | null): boolean {
  return !!(eddValue && eddValue.source && eddValue.first);
}

/**
 * Determines if EDD represents a time range (different start and end times)
 *
 * @param eddValue - The EDD value object to check
 * @returns true if this is a time range, false otherwise
 */
export function isEddTimeRange(eddValue: EddSource | null): boolean {
  return !!(
    eddValue?.last &&
    eddValue?.first &&
    eddValue.last.time !== eddValue.first.time
  );
}
